import React, {useContext, useState} from 'react';
import {imgExtensions} from "../../../../../../components/dropzone/dropzone.jsx";
import {CitizenContext} from "../context/citizen-context.jsx";
import {Card} from "../../../../../../components/card/card.jsx";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import ModalAttaches from "../../../../../../components/modal/modal-attaches.jsx";
import ViolenceType from "../../utils/violence-type.js";
import LifeRisk from "../../utils/life-risk.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import optionsUtils from "../../../../../../utils/options-utils.js";
import moment from "moment";
import ProtectionTypes from "../../utils/protection-type.js";
import CitizenCredentialTable from "./citizen-credential-table.jsx";
import PrintTemplate from "../../../../../../components/reports/print-reports/print-template.jsx";
import useHasPermission from "../../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../../utils/enums/permission-enum.js";
import AggressorSubpoena from "../../utils/aggressor-subpoena.js";
import AggressorRestriction from "../../utils/aggressor-restriction.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const API_URL = import.meta.env.VITE_API_URL;

const CitizenResume = ({printRef, headerSelected}) => {
    const {citizen, fetchData, aggressors, attachesDescription, attaches, files, onlyView} = useContext(CitizenContext);

    const hasPermission = useHasPermission();
    const [modal, setModal] = useState({show: false, file: null});

    const findValue = (value, data) => {
        if (!value) return "";
        return data.find(d => d.value.toString() === value.toString())?.label ?? "";
    }

    const openModal = (attachId) => {
        if (!!attachId && (!onlyView || hasPermission(PermissionEnum.SOS_ATTACHS_SHOW))) {
            const file = attaches.find(a => a.attachId === attachId) ?? {attachId, extension: "jpeg"};

            setModal({show: true, file});
        }
    }

    const closeModal = () => setModal({show: false, file: undefined});

    const attachSrc = (attachId, fallback) => {
        if (!attachId) return fallback;

        const attach = attaches.find(a => a.attachId === attachId);
        return attach?.new ? attach.url : `${API_KEEPER}/ged/attach/${attachId}`;
    }

    const address = (item, isCitizen = false) => {
        return (
            <div className={"grid grid-cols-12 gap-3"} key={"addresses-" + item.id}>
                <div className={"col-span-4"}>
                    <div className={"image"}>
                        <img
                            onClick={() => openModal(item.attachId)}
                            src={(!item.imgScr && item.attachId) ? `${API_KEEPER}/ged/attach/${item.attachId}` : item.imgScr ? item.imgScr : getAssetPath("img/no-img.png")}
                            alt=""
                        />
                    </div>
                </div>
                <div className={"col-span-8 grid grid-cols-2 gap-3 self-start"}>
                    <p className={"col-span-2"}>Local pré-cadastrado: <span className={"info"}>{findValue(item.placeId, fetchData.places)}</span></p>
                    <p>Tipo: <span className={"info"}>{item.addressType}</span></p>
                    <p>Logradouro: <span className={"info"}>{item.address}</span></p>
                    <p>Número: <span className={"info"}>{item.number}</span></p>
                    <p>Bairro: <span className={"info"}>{item.neighborhood}</span></p>
                    <p className={"col-span-2"}>Complemento: <span className={"info"}>{item.complement}</span></p>
                    <p>Município: <span className={"info"}>{item.city}</span></p>
                    <p>CEP: <span className={"info"}>{item.zipCode}</span></p>
                    <p>Identificação: <span className={"info"}>{item.description}</span></p>
                    <p>Setor: <span className={"info"}>{item.sector}</span></p>
                    <p>Latitude: <span className={"info"}>{item.latitude}</span></p>
                    <p>Longitude: <span className={"info"}>{item.longitude}</span></p>
                    {
                        isCitizen &&
                        <>
                            <p>Visita: <span className={"info"}>{item.visitable ? "Sim" : "Não"}</span></p>
                            <p>Ronda: <span className={"info"}>{item.patrollable ? "Sim" : "Não"}</span></p>
                            <p>Horário inicial: <span className={"info"}>{item.availabilityStartTime}</span></p>
                            <p>Horário final: <span className={"info"}>{item.availabilityEndTime}</span></p>
                        </>
                    }
                </div>
            </div>
        )
    }

    const vehicle = (item) => {
        return (
            <div className={"grid grid-cols-12 gap-3"} key={"vehicles-" + item.id}>
                <div className={"col-span-4"}>
                    <div className={"image"}>
                        <img
                            onClick={() => openModal(item.attachId)}
                            src={attachSrc(item.attachId, getAssetPath("img/no-img.png"))}
                            alt=""
                        />
                    </div>
                </div>
                <div className={"col-span-8 grid grid-cols gap-3 self-start"}>
                    <p>Placa: <span className={"info"}>{projectUtils.formatPlate(item.plate)}</span></p>
                    <p>Descrição: <span className={"info"}>{item.description}</span></p>
                </div>
            </div>
        )
    }

    const isCpf = citizen.type === "CPF";

    return (
        <>
            <Card className={"w-5xl justify-self-center"}>
                <Card.Body className={"!p-6"}>
                    <PrintTemplate browserTitle={'SOS Cadastro'} title={`SOS Cadastro - ${citizen.name}`} ref={printRef} entity={headerSelected} printClassName={"resume-content"}>
                        <div className={"space-y-4"}>
                            <article id={"citizen-info"} className={"space-y-4"}>
                                <h2 className={"resume-title"}>
                                    Identificação {isCpf ? "do protegido" : "da empresa"}
                                </h2>

                                <section className={"grid grid-cols-12 gap-3"}>
                                    <div className={"col-span-4"}>
                                        <div className={"image"}>
                                            <img
                                                onClick={() => openModal(citizen.victimPicture)}
                                                src={(citizen.victimPicture && !citizen.imgScr) ? `${API_KEEPER}/ged/attach/${citizen.victimPicture}` : citizen.imgScr ? citizen.imgScr : getAssetPath("img/user.png")}
                                                alt=""
                                            />
                                        </div>
                                    </div>
                                    <div className={"col-span-8 grid grid-cols-2 gap-3 self-start"}>
                                        {
                                            citizen.id &&
                                            <>
                                                <p>Status: <span className={"info"}>{citizen.status === "ACTIVATED" ? "Ativo" : "Inativo"}</span></p>
                                                <p>App Sentry SOS: <span className={"info"}>{!citizen.noApp ? "Ativo" : "Inativo"}</span></p>
                                            </>
                                        }
                                        <p className={"col-span-2"}>Programa: <span className={"info"}>{citizen?.protectionProgram?.map(p => p?.label ?? p).join(", ") ?? ""}</span></p>
                                        <p>{isCpf ? "Nome" : "Razão social"}: <span className={"info"}>{citizen.name}</span></p>
                                        {
                                            isCpf &&
                                            <>
                                                <p>Nome social: <span className={"info"}>{citizen.socialName}</span></p>
                                                <p>CPF: <span className={"info"}>{projectUtils.formatCpfToView(citizen.cpf)}</span></p>
                                                <p>Nascimento: <span className={"info"}>{citizen.dtBirth ? moment(citizen.dtBirth).format("DD/MM/YYYY") : null}</span></p>
                                                <p>Sexo: <span className={"info"}>{findValue(citizen.sex, optionsUtils.sex)}</span></p>
                                            </>
                                        }
                                        {
                                            !isCpf &&
                                            <>
                                                <p>Nome fantasia: <span className={"info"}>{citizen.aliasName}</span></p>
                                                <p className={"col-span-2"}>CNPJ: <span className={"info"}>{projectUtils.formatCnpjToView(citizen.cnpj)}</span></p>
                                            </>
                                        }
                                        <p>Telefone: <span className={"info"}>{projectUtils.formatPhone(citizen.phone)}</span></p>
                                        <p>E-mail: <span className={"info"}>{citizen.email}</span></p>
                                        <p>
                                            Número de identificação: <span className={"info"}>{citizen.identificationNumber}</span>
                                        </p>
                                        <p>
                                            Grau de risco: <span className={"info"}>{LifeRisk.toView(citizen.lifeRisk)}</span>
                                        </p>
                                    </div>
                                </section>

                                {
                                    citizen.protectiveMeasure &&
                                    <section>
                                        <h3 className={"sub-title"}>Dados adicionais</h3>

                                        <section className={"grid grid-cols-3 gap-3"}>
                                            <p>
                                                Número de filhos: <span className={"info"}>{citizen.childCount}</span>
                                            </p>
                                            <p>
                                                Escolaridade: <span className={"info"}>{findValue(citizen.educationId, fetchData.educations)}</span>
                                            </p>
                                            <p>
                                                Nacionalidade: <span className={"info"}>{citizen.nationality}</span>
                                            </p>
                                            <p>
                                                Raça ou etnia: <span className={"info"}>{findValue(citizen.raceEthnicityId, fetchData.races)}</span>
                                            </p>
                                            <p>
                                                Religião: <span className={"info"}>{findValue(citizen.religionId, fetchData.religions)}</span>
                                            </p>
                                            <p>
                                                Naturalidade: <span className={"info"}>{citizen.cityBirth}</span>
                                            </p>
                                        </section>
                                    </section>
                                }

                                {
                                    citizen.id &&
                                    <section>
                                        <h3 className={"sub-title"}>Funcionamento do aplicativo</h3>

                                        <CitizenCredentialTable
                                            credentials={citizen?.credentials}
                                            domLayout='autoHeight'
                                        />
                                    </section>
                                }

                            </article>

                            {
                                !!citizen.addresses.length &&
                                <article className={"space-y-3"}>
                                    <h2 className={"resume-title"}>Endereços {isCpf ? "do protegido" : "da empresa"}</h2>

                                    {
                                        citizen.addresses.map((item) => address(item, true))
                                    }
                                </article>
                            }

                            {
                                !!citizen.contactPhones.length &&
                                <article className={"space-y-3"}>
                                    <h2 className={"resume-title"}>Contatos {isCpf ? "do protegido" : "da empresa"}</h2>

                                    {
                                        citizen.contactPhones.map(c => (
                                            <div className={"grid grid-cols-2 gap-3"} key={c.id}>
                                                <p>Telefone: <span className={"info"}>{projectUtils.formatPhone(c.phone)}</span></p>
                                                <p>Descrição: <span className={"info"}>{c.description}</span></p>
                                            </div>
                                        ))
                                    }
                                </article>
                            }

                            {
                                !!citizen.vehicles.length &&
                                <article className={"space-y-3"}>
                                    <h2 className={"resume-title"}>Veículos {isCpf ? "do protegido" : "da empresa"}</h2>

                                    {
                                        citizen.vehicles.map((item) => vehicle(item))
                                    }
                                </article>
                            }

                            {
                                !!files.length &&
                                <article id={"protective-measure-attaches"} className={"space-y-3"}>
                                    <h2 className={"resume-title"}>
                                        Anexos
                                    </h2>

                                    <section className={"grid grid-cols-4 gap-3"}>
                                        {
                                            files.map(file => {
                                                const ext = file.type ?? file.extension
                                                let url = !file.new ? API_KEEPER + "/ged/attach/" + file.attachId : file.url;
                                                if (!imgExtensions.includes(ext)) {
                                                    url = API_URL + "/public/assets/img/file-types/" + file.name?.split('.').pop() + '.png';
                                                }

                                                return (
                                                    <div key={file.attachId}>
                                                        <div className={"attach"}>
                                                            <img onClick={() => openModal(file.attachId)} src={url} alt=""/>
                                                        </div>
                                                        <p className={"info text-center"}>{attachesDescription[file.attachId]}</p>
                                                    </div>
                                                )
                                            })
                                        }
                                    </section>
                                </article>
                            }

                            {
                                citizen.protectiveMeasure && aggressors.map((aggressor, i) => (
                                    <article id={"aggressor-" + i} className={"space-y-3"} key={aggressor.id}>
                                        <h2 className={"resume-title"}>
                                            Agressor {i + 1}
                                        </h2>

                                        <section className={"grid grid-cols-12 gap-3"}>
                                            <div className={"col-span-4"}>
                                                <div className={"image"}>
                                                    <img
                                                        onClick={() => openModal(aggressor.attachId)}
                                                        src={(!aggressor.imgScr && aggressor.attachId) ? `${API_KEEPER}/ged/attach/${aggressor.attachId}` : aggressor.imgScr ? aggressor.imgScr : getAssetPath("img/user.png")}
                                                        alt=""
                                                    />
                                                </div>
                                            </div>
                                            <div className={"col-span-8 grid grid-cols-2 gap-3 self-start"}>
                                                <p className={"col-span-2"}>Nome: <span className={"info"}>{aggressor.name}</span></p>
                                                <p>Nome social: <span className={"info"}>{aggressor.socialName}</span></p>
                                                <p>CPF: <span className={"info"}>{projectUtils.formatCpfToView(aggressor.cpf)}</span></p>
                                                <p>Nascimento: <span className={"info"}>{aggressor.dtBirth ? moment(aggressor.dtBirth).format("DD/MM/YYYY") : null}</span></p>
                                                <p>Nacionalidade: <span className={"info"}>{aggressor.nationality}</span></p>
                                                <p>Naturalidade: <span className={"info"}>{aggressor.cityBirth}</span></p>
                                                <p>Escolaridade: <span className={"info"}>{findValue(aggressor.educationId, fetchData.educations)}</span></p>
                                                <p className={"col-span-2"}>Tipo de medida: <span className={"info"}>{ProtectionTypes.toView(aggressor.protectionType)}</span></p>
                                                <p>Vínculo com protegido: <span className={"info"}>{findValue(aggressor.familyConnectionId, fetchData.familyConnections)}</span></p>
                                                <p>Mora com o protegido: <span className={"info"}>{aggressor.livesWithProtected ? "Sim" : "Não"}</span></p>
                                                <p className={"col-span-2"}>Tipo de violência: <span className={"info"}>{ViolenceType.optionsToView(aggressor.violenceTypes)}</span></p>
                                                <p>Histórico de uso de arma de fogo: <span className={"info"}>{aggressor.firearmUseHistory ? "Sim" : "Não"}</span></p>
                                                <p>Participa de Grupo Reflexivo: <span className={"info"}>{aggressor.reflectiveGroup ? "Sim" : "Não"}</span></p>
                                            </div>

                                            <div className={"col-span-12 grid grid-cols-12 gap-3 self-start"}>
                                                <p className={"col-span-4"}>Número do processo: <span className={"info"}>{aggressor.processNumber}</span></p>
                                                <p className={"col-span-4"}>Data de expedição: <span className={"info"}>{aggressor.dtExpedition ? moment(aggressor.dtExpedition).format("DD/MM/YYYY") : null}</span></p>
                                                <p className={"col-span-4"}>Data de expiração: <span className={"info"}>{aggressor.dtExpiration ? moment(aggressor.dtExpiration).format("DD/MM/YYYY") : null}</span></p>
                                                <p className={"col-span-4"}>Prazo Indeterminado: <span className={"info"}>{aggressor.hasNoExpiration ? "Sim" : "Não"}</span></p>
                                                <p className={"col-span-4"}>Distanciamento (metros): <span className={"info"}>{aggressor.minDistance}</span></p>
                                                <p className={"col-span-4"}>Vara criminalista: <span className={"info"}>{aggressor.criminalCourt}</span></p>
                                                <p className={"col-span-6"}>Mandado de Intimação: <span className={"info"}>{AggressorSubpoena.optionsToView(aggressor.subpoena)}</span></p>
                                                <p className={"col-span-6"}>Mandado de Prisão: <span className={"info"}>{aggressor.arrestWarrant ? "Sim" : "Não"}</span></p>
                                                <p className={"col-span-12"}>Restrições do agressor: <span className={"info"}>{AggressorRestriction.optionsToView(aggressor.restrictions)}</span></p>
                                                <p className={"col-span-12"}>Informações adicionais: <span className={"info"}>{aggressor.description}</span></p>
                                            </div>
                                        </section>
                                        {
                                            !!aggressor?.addresses?.length &&
                                            <section className={"space-y-3"}>
                                                <h3>Endereços do agressor {i + 1}</h3>

                                                {
                                                    aggressor?.addresses?.map((item) => address(item))
                                                }
                                            </section>
                                        }

                                        {
                                            !!aggressor?.vehicles?.length &&
                                            <section className={"space-y-3"}>
                                                <h3>Veículos do agressor {i + 1}</h3>

                                                {
                                                    aggressor.vehicles.map((item) => vehicle(item))
                                                }
                                            </section>
                                        }
                                    </article>
                                ))
                            }

                            <article id={"observation"}>
                                <h2 className={"resume-title"}>
                                    Observação
                                </h2>
                                <section>
                                    <p className={"info !font-normal break-all!"}>{citizen.observation}</p>
                                </section>
                            </article>
                        </div>
                    </PrintTemplate>
                </Card.Body>
            </Card>

            <ModalAttaches
                open={modal.show}
                setOpen={closeModal}
                file={modal.file}
            />
        </>
    );
};

export default CitizenResume;